/**
 * Pure calculation helpers extracted from timeline drag hooks for testability.
 * These functions encapsulate the frame-clamping and constraint logic
 * that prevents interaction regressions.
 */

import type { KeyframeInfo } from "../utils/keyframeHelpers";
import { getKeyframePropertyPath } from "../utils/keyframeHelpers";
import {
  MIN_SCENE_DURATION_FRAMES,
  MAX_EXTENSION_MULTIPLIER,
} from "../utils/sceneTrimConstants";

// -- Layer edge drag --------------------------------------------------------

const MIN_LAYER_LENGTH_FRAMES = 1;

/**
 * Clamps a frame value when dragging the left (ip) edge of a layer.
 * Prevents ip from exceeding op - MIN_LAYER_LENGTH_FRAMES or going below 0.
 */
export function clampLeftEdge(frame: number, op: number): number {
  return Math.max(0, Math.min(frame, op - MIN_LAYER_LENGTH_FRAMES));
}

/**
 * Clamps a frame value when dragging the right (op) edge of a layer.
 * Prevents op from going before ip + MIN_LAYER_LENGTH_FRAMES.
 */
export function clampRightEdge(frame: number, ip: number): number {
  return Math.max(ip + MIN_LAYER_LENGTH_FRAMES, frame);
}

export interface LayerEdgeDragTiming {
  ip: number;
  op: number;
}

export function getLayerEdgeDragDeltaBounds(
  layers: LayerEdgeDragTiming[],
  edge: "left" | "right"
): { minDelta: number; maxDelta: number } {
  if (!layers.length) {
    return { minDelta: 0, maxDelta: 0 };
  }

  let minDelta = -Infinity;
  let maxDelta = Infinity;

  layers.forEach(({ ip, op }) => {
    if (edge === "left") {
      minDelta = Math.max(minDelta, -ip);
      maxDelta = Math.min(maxDelta, op - MIN_LAYER_LENGTH_FRAMES - ip);
    } else {
      minDelta = Math.max(minDelta, ip + MIN_LAYER_LENGTH_FRAMES - op);
    }
  });

  if (minDelta > maxDelta) {
    return { minDelta: 0, maxDelta: 0 };
  }

  return { minDelta, maxDelta };
}

export function clampLayerEdgeDragDelta(
  delta: number,
  minDelta: number,
  maxDelta: number
): number {
  return Math.max(minDelta, Math.min(maxDelta, delta));
}

// -- Layer body drag --------------------------------------------------------

/**
 * Computes the new ip/op and timing delta for a layer body drag.
 * Returns null when the drag produces no change.
 */
export function computeBodyDragResult(
  newIp: number,
  initialIp: number,
  initialOp: number
): { ip: number; op: number; delta: number } | null {
  const clamped = Math.max(0, Math.round(newIp));
  const delta = clamped - initialIp;
  if (delta === 0) return null;
  return { ip: clamped, op: initialOp + delta, delta };
}

/**
 * Body dragging persists the layer window shift.
 * The player API shifts the selected layer timing and absolute keyframe times
 * together; descendants move through their parent timeline offset.
 */

// -- Keyframe drag ----------------------------------------------------------

/**
 * Finds the time constraints for a dragged keyframe based on its
 * adjacent siblings within the same property.
 */
export function findKeyframeTimeConstraints(
  keyframe: KeyframeInfo,
  allKeyframes: KeyframeInfo[]
): { minTime: number; maxTime: number } {
  const propertyPath = getKeyframePropertyPath(keyframe.path);

  const samePropertyKeyframes = allKeyframes
    .filter((kf) => getKeyframePropertyPath(kf.path) === propertyPath)
    .sort((a, b) => a.keyframeIndex - b.keyframeIndex);

  const currentIndex = keyframe.keyframeIndex;
  const prevKf = samePropertyKeyframes.find(
    (kf) => kf.keyframeIndex === currentIndex - 1
  );
  const nextKf = samePropertyKeyframes.find(
    (kf) => kf.keyframeIndex === currentIndex + 1
  );

  const minTime = prevKf ? prevKf.t + 1 : 0;
  const maxTime = nextKf ? nextKf.t - 1 : Infinity;

  return { minTime, maxTime };
}

/**
 * Clamps a keyframe time to the given constraints.
 */
export function clampKeyframeTime(
  time: number,
  minTime: number,
  maxTime: number
): number {
  return Math.max(minTime, Math.min(maxTime, time));
}

export interface KeyframeDragConstraint {
  initialTime: number;
  minTime: number;
  maxTime: number;
}

export interface SelectableKeyframeDragConstraint {
  id: string;
  groupKey: string;
  keyframeIndex: number;
  initialTime: number;
}

/**
 * Derives the shared delta bounds for a multi-keyframe drag.
 * Every selected keyframe must stay within its own local constraints, so the
 * allowed drag range is the intersection of all per-keyframe ranges.
 */
export function getMultiKeyframeDragBounds(
  keyframes: KeyframeDragConstraint[]
): { minDelta: number; maxDelta: number } {
  if (!keyframes.length) {
    return { minDelta: 0, maxDelta: 0 };
  }

  let minDelta = -Infinity;
  let maxDelta = Infinity;

  keyframes.forEach(({ initialTime, minTime, maxTime }) => {
    minDelta = Math.max(minDelta, minTime - initialTime);
    maxDelta = Math.min(maxDelta, maxTime - initialTime);
  });

  if (minDelta > maxDelta) {
    return { minDelta: 0, maxDelta: 0 };
  }

  return { minDelta, maxDelta };
}

/**
 * Derives shared delta bounds for a selected keyframe group.
 * Selected keyframes in the same property move together, so they should not
 * constrain each other. Only the nearest unselected siblings block the move.
 */
export function getSelectedKeyframeDragBounds(
  keyframes: SelectableKeyframeDragConstraint[],
  selectedIds: Set<string>
): { minDelta: number; maxDelta: number } {
  const selectedKeyframes = keyframes.filter((keyframe) =>
    selectedIds.has(keyframe.id)
  );

  if (!selectedKeyframes.length) {
    return { minDelta: 0, maxDelta: 0 };
  }

  const groupedKeyframes = new Map<
    string,
    SelectableKeyframeDragConstraint[]
  >();
  keyframes.forEach((keyframe) => {
    const group = groupedKeyframes.get(keyframe.groupKey) ?? [];
    group.push(keyframe);
    groupedKeyframes.set(keyframe.groupKey, group);
  });

  groupedKeyframes.forEach((group, groupKey) => {
    groupedKeyframes.set(
      groupKey,
      [...group].sort((left, right) => left.keyframeIndex - right.keyframeIndex)
    );
  });

  return getMultiKeyframeDragBounds(
    selectedKeyframes.map((keyframe) => {
      const group = groupedKeyframes.get(keyframe.groupKey) ?? [];
      const groupIndex = group.findIndex((entry) => entry.id === keyframe.id);
      const previousKeyframe =
        groupIndex > 0
          ? [...group]
              .slice(0, groupIndex)
              .reverse()
              .find((entry) => !selectedIds.has(entry.id))
          : undefined;
      const nextKeyframe =
        groupIndex >= 0
          ? group
              .slice(groupIndex + 1)
              .find((entry) => !selectedIds.has(entry.id))
          : undefined;

      return {
        initialTime: keyframe.initialTime,
        minTime: previousKeyframe ? previousKeyframe.initialTime + 1 : 0,
        maxTime: nextKeyframe ? nextKeyframe.initialTime - 1 : Infinity,
      };
    })
  );
}

/**
 * Clamps a shared drag delta for a multi-keyframe move.
 */
export function clampKeyframeDragDelta(
  delta: number,
  minDelta: number,
  maxDelta: number
): number {
  return Math.max(minDelta, Math.min(maxDelta, delta));
}

// -- Scene trim drag --------------------------------------------------------

/**
 * Clamps a scene trim end frame within the allowed bounds.
 * compositionStart offsets the absolute frame to composition space.
 */
export function clampSceneTrimEnd(
  absoluteFrame: number,
  compositionStart: number,
  totalFrames: number
): number {
  const maxExtensionFrames = totalFrames * MAX_EXTENSION_MULTIPLIER;
  return Math.max(
    compositionStart + MIN_SCENE_DURATION_FRAMES,
    Math.min(absoluteFrame, compositionStart + maxExtensionFrames)
  );
}

/**
 * Converts a preview trim-end (absolute frame) to the relative frame
 * used in the trim mod.
 */
export function trimEndToRelative(
  absoluteFrame: number,
  compositionStart: number
): number {
  return absoluteFrame - compositionStart;
}
